import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

const Section = ({ title, children }) => (
  <motion.div
    className="space-y-4"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <h2 className="text-white font-black text-lg uppercase tracking-widest">{title}</h2>
    <div className="text-slate-400 text-sm leading-relaxed space-y-3">{children}</div>
  </motion.div>
);

const PrivacyPolicy = () => (
  <div className="min-h-screen pt-32 pb-24 px-6">
    <Helmet>
      <title>Privacy Policy | VideoExamples</title>
      <meta name="description" content="VideoExamples privacy policy — we do not collect, store, or share your personal data." />
    </Helmet>

    <div className="max-w-2xl mx-auto space-y-14">
      <motion.header
        className="space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <p className="text-orange-500 text-[10px] font-black uppercase tracking-[0.3em]">Legal</p>
        <h1 className="text-5xl font-black text-white uppercase tracking-tighter leading-none">
          Privacy Policy
        </h1>
        <p className="text-slate-500 text-sm">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </motion.header>

      <div className="w-full h-px bg-white/5" />

      <Section title="No Sales Contact. Ever.">
        <p>
          VideoExamples is a curation tool, not a sales funnel. No salesperson will ever contact you through VideoExamples — not now, not ever. We have zero interest in your inbox.
        </p>
        <p>
          Our entire purpose is to help you make a better, more informed decision about video production — with zero pressure attached.
        </p>
      </Section>

      <Section title="Your Data Stays in Your Browser">
        <p>
          Any data you choose to provide while using VideoExamples — such as filter preferences or session state — is stored locally in your browser only. We do not collect it, transmit it, or store it on any server.
        </p>
        <p>
          We do not maintain user accounts, email lists, or CRM records of any kind. Your visit here is entirely your own.
        </p>
      </Section>

      <Section title="The One Tracker We Use">
        <p>
          There is a single analytics tracker on this website. Its sole purpose is to help us understand how many people visit and which pages are most useful. All data collected through this tracker is <strong className="text-white font-bold">completely anonymous</strong> — we cannot identify you individually, and we do not try to.
        </p>
        <p>
          No personal identifiers, no fingerprinting, no cross-site tracking.
        </p>
      </Section>

      <Section title="Third-Party Links">
        <p>
          VideoExamples links to external websites — production agencies, portfolio pages, Clutch profiles, and more. Once you leave VideoExamples and visit one of these external sites, their own privacy policies and tracking practices apply.
        </p>
        <p>
          We are not responsible for the data collection, outreach, or tracking behavior of any third-party website. Please review their privacy policies before submitting any personal information to them.
        </p>
      </Section>

      <Section title="Questions">
        <p>
          If you have any questions about this policy, you can reach the team behind VideoExamples at{' '}
          <a
            href="https://www.whatastory.agency"
            target="_blank"
            rel="noopener noreferrer"
            className="text-orange-400 underline underline-offset-2 hover:text-orange-300 transition-colors"
          >
            What a Story
          </a>.
        </p>
      </Section>
    </div>
  </div>
);

export default PrivacyPolicy;